/**
 * Frontend-Konfiguration zur Laufzeit.
 * Reihenfolge: window.__APP_CONFIG__ (über /config.js) > import.meta.env > Defaults.
 */

export interface AppConfig {
  oidcIssuer: string;
  oidcClientId: string;
  apiBaseUrl: string;
  appEnv: 'dev' | 'prod' | 'unknown';
}

interface RuntimeWindow {
  __APP_CONFIG__?: Partial<AppConfig>;
}

function readRuntime(): Partial<AppConfig> {
  if (typeof window === 'undefined') return {};
  const w = window as unknown as RuntimeWindow;
  return w.__APP_CONFIG__ ?? {};
}

function pickEnv(name: string): string | undefined {
  const env = (import.meta.env as unknown as Record<string, string | undefined>)[name];
  return env && env.length > 0 ? env : undefined;
}

function isPlaceholder(value: string | undefined): boolean {
  if (!value) return true;
  return value.startsWith('${') && value.endsWith('}');
}

function pick(runtimeKey: keyof AppConfig, envKey: string, fallback: string): string {
  const runtime = readRuntime()[runtimeKey];
  if (typeof runtime === 'string' && !isPlaceholder(runtime)) return runtime;
  const env = pickEnv(envKey);
  if (env && !isPlaceholder(env)) return env;
  return fallback;
}

function pickEnvName(): AppConfig['appEnv'] {
  const v = pick('appEnv', 'VITE_APP_ENV', 'dev');
  if (v === 'prod' || v === 'dev') return v;
  return 'unknown';
}

export const appConfig: AppConfig = {
  oidcIssuer: pick(
    'oidcIssuer',
    'VITE_OIDC_ISSUER',
    'https://auth.test.alexander-paul.de/realms/lft-dev',
  ),
  oidcClientId: pick('oidcClientId', 'VITE_OIDC_CLIENT_ID', 'lft-frontend'),
  apiBaseUrl: pick('apiBaseUrl', 'VITE_API_BASE_URL', '/api/v1'),
  appEnv: pickEnvName(),
};
